import React from 'react';
import Panel from '../Panel/Panel';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { AppBar, Button, IconButton, Paper, Tabs, Tab } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import Chip from '@mui/material/Chip';

const StyledTabs = styled((props) => <Tabs {...props} />)((({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
  },
  '& .MuiTabs-scrollButtons.Mui-disabled': {
    opacity: 0.3
  }
})));

const StyledTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: 'none',
  minWidth: 0,
  [theme.breakpoints.up('sm')]: {
    minWidth: 0,
  },
  fontWeight: theme.typography.fontWeightRegular,
  marginRight: theme.spacing(1),
  color: theme.palette.text.secondary,
  fontFamily: [
    'Poppins',
  ].join(','),
  '&:hover': {
    color: theme.palette.secondary.main,
    opacity: 1,
  },
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
  },
  '&.Mui-focusVisible': {
    backgroundColor: theme.palette.action.focus,
  },
  '&.info-tab': {
    // Info state - black text with info icon (before validation)
    color: theme.palette.text.primary,
    '&.Mui-selected': {
      color: theme.palette.text.primary,
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
  '&.error-tab': {
    color: theme.palette.error.main,
    '&:hover': {
      color: theme.palette.error.dark,
    },
    '&.Mui-selected': {
      color: theme.palette.error.main,
      fontWeight: theme.typography.fontWeightMedium,
      borderBottom: `2px solid ${theme.palette.error.main}`,
      marginBottom: '-1px',
    },
  },
  '&.success-tab': {
    color: theme.palette.success.main,
    '&:hover': {
      color: theme.palette.success.dark,
    },
    '&.Mui-selected': {
      color: theme.palette.success.main,
      fontWeight: theme.typography.fontWeightMedium,
      borderBottom: `2px solid ${theme.palette.success.main}`,
      marginBottom: '-1px',
    },
  },
}));

const VerticalTabs = styled((props) => <Tabs {...props} />)((({ theme }) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
  width: 125,
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.secondary.main,
  }
})));

const VerticalTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
  root: {
    textTransform: 'none',
    minWidth: 72,
    fontWeight: theme.typography.fontWeightMedium,
    marginRight: theme.spacing(1),
    fontFamily: [
      'Poppins',
    ].join(','),
    '&:hover': {
      color: theme.palette.primary.main,
    },
    '&.Mui-selected': {
      color: theme.palette.secondary.main,
      fontWeight: theme.typography.fontWeightBold,
    },
    '&.Mui-focusVisible': {
      color: theme.palette.secondary.main,
    },
  },
  selected: {},
  wrapper: {
    alignItems: 'flex-end',
  },
}));

const ButtonInTabs = ({children, className, disabled, key, onClick }) => {
  return <Button className={className} disabled={disabled} key={key} onClick={onClick} children={children} />;
};

export function EvoraTabs(props) {
  const { tabLabels, tabKey, changeTab, endAdornment } = props;
  return (
    <AppBar position="static" color='transparent' elevation={0} sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <StyledTabs
            value={tabKey}
            onChange={changeTab}
            aria-label="navigation-tabs"
            scrollButtons="auto"
            variant="scrollable"
            sx={{ flexGrow: 1 }}
        >
          {tabLabels.map((value, index) => {
            if((value.type != null && value.type !== '') && value.type === 'button'){
              return <ButtonInTabs
                              onClick={value.onClick}
                              key={'tabButton-' + index}
                              disabled={value.disabled}
                            >
                              {value.icon}
                              {value.name}
                          </ButtonInTabs>
            }else {
              // Determine icon and class based on success/error/info state
              let tabIcon = null;
              let tabClass = '';
              if (value.success) {
                tabIcon = <CheckCircleIcon fontSize="small" />;
                tabClass = 'success-tab';
              } else if (value.error) {
                tabIcon = <WarningIcon fontSize="small" />;
                tabClass = 'error-tab';
              } else if (value.info) {
                tabIcon = <InfoOutlinedIcon fontSize="small" />;
                tabClass = 'info-tab';
              }

              const tabLabel = (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {tabIcon}
                  {value.name}
                  {value.count != null && value.count > 0 && (
                    <Chip
                      label={value.count}
                      size="small"
                      color={value.countColor || 'primary'}
                      sx={{
                        height: 18,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  )}
                  {value.onClose && (
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); value.onClose(); }}
                      sx={{ ml: 0.5, p: 0.25 }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              );

              return (
                <StyledTab
                  key={index}
                  label={tabLabel}
                  id={`evora-tab-${index}`}
                  disabled={value.disabled}
                  className={tabClass}
                  data-guide={value.dataGuide}
                />
              );
            }
          })}
        </StyledTabs>
        {endAdornment}
      </Box>
    </AppBar>
  );
}

export function EvoraVerticalTabs(props) {
  const { TabContents, tabLabels, tabKey, changeTab } = props;
  return (
      <div style={{ flexGrow: 1, display: 'flex' }}>
          <VerticalTabs 
                value={tabKey} 
                onChange={changeTab} 
                aria-label="vertical-tabs" 
                scrollButtons="auto" 
                variant="scrollable" 
                orientation="vertical"
          >
            {tabLabels.map((value, index) => {
              if((value.type != null && value.type !== '') && value.type === 'button'){
                return <ButtonInTabs
                            onClick={value.onClick}
                            key={'vertTabButton-' + index}
                            disabled={value.disabled}
                          >
                            {value.icon}
                            {value.name}
                        </ButtonInTabs>
              }else {
                return <VerticalTab key={'vertTab-' + value.name + '-' + index} label={value.name} disabled={value.disabled} />
              }              
            })}
          </VerticalTabs>
          <TabContents />
      </div>      
  );
}

export function TabPanel(props) {
    const children = props != null ? props.children : null;
    const value = props != null ? props.value : null;
    const index = props != null ? props.index : null;
    const { ...other } = props != null ? props : {};
    const isActive = value === index;
  return (
  <div
    role="tabpanel"
    hidden={!isActive}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    style={{ display: isActive ? 'block' : 'none' }}
    {...other}
  >
    <Panel>
      {children}
    </Panel>
  </div>
  );
}

export function VerticalTabPanel(props) {
  const { children, value, index, ...other } = props;
  const isActive = value === index;
  return (
  <div
    role="tabpanel"
    hidden={!isActive}
    id={`vertical-tabpanel-${index}`}
    aria-labelledby={`vertical-tab-${index}`}
    {...other}
    style={{
      width: '85%',
      padding: 10,
      borderRadius: '4px',
      transition: 'boxShadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      display: isActive ? 'block' : 'none',
    }}
  >
    <Paper elevation={3}>
      {children}
    </Paper>
  </div>
  );
}

export function TabCanvas(props) {
  const { children, value, index, ...other } = props;
  const isActive = value === index;
  return (
  <div
    role="tabpanel"
    hidden={!isActive}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    style={{ display: isActive ? 'block' : 'none' }}
    {...other}
  >
    {children}
  </div>
  );
}

export function VerticalTabCanvas(props) {
  const { children, value, index, ...other } = props;
  const isActive = value === index;
  return (
  <div
    role="tabpanel"
    hidden={!isActive}
    id={`vertical-tabpanel-${index}`}
    aria-labelledby={`vertical-tab-${index}`}
    {...other}
    style={{
      width: '85%',
      padding: 10,
      borderRadius: '4px',
      transition: 'boxShadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      display: isActive ? 'block' : 'none',
    }}
  >
    {children}
  </div>
  );
}